import { createBrowserClient } from '@supabase/ssr'

// Singleton instance
let supabaseClient: ReturnType<typeof createBrowserClient> | null = null

/**
 * Create a Supabase client for browser/client components with cookie-based auth
 *
 * Uses cookie-based session storage to work with server-side auth.
 * The browser client reads session from cookies set by the server.
 */
export function createClient() {
  // Return existing client if available
  if (supabaseClient) {
    return supabaseClient
  }

  const url = process.env.NEXT_PUBLIC_SUPABASE_URL
  const key = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY

  if (!url || !key) {
    // During build, these might not be available
    if (typeof window === 'undefined') {
      return {} as ReturnType<typeof createBrowserClient>
    }
    throw new Error('Missing Supabase environment variables')
  }

  // Create client with cookie storage for SSR compatibility
  supabaseClient = createBrowserClient(url, key, {
    cookies: {
      get(name: string) {
        if (typeof document === 'undefined') return undefined
        const cookies = document.cookie.split('; ')
        const cookie = cookies.find(c => c.startsWith(`${name}=`))
        return cookie?.split('=')[1]
      },
      set(name: string, value: string, options: any) {
        if (typeof document === 'undefined') return

        let cookie = `${name}=${value}`
        if (options?.maxAge) cookie += `; max-age=${options.maxAge}`
        if (options?.path) cookie += `; path=${options.path}`
        if (options?.domain) cookie += `; domain=${options.domain}`
        if (options?.sameSite) cookie += `; samesite=${options.sameSite}`
        if (options?.secure) cookie += '; secure'

        document.cookie = cookie
      },
      remove(name: string, options: any) {
        if (typeof document === 'undefined') return

        let cookie = `${name}=; max-age=0`
        if (options?.path) cookie += `; path=${options.path}`
        if (options?.domain) cookie += `; domain=${options.domain}`

        document.cookie = cookie
      },
    }
  })

  return supabaseClient
}

// Export singleton instance
export const supabase = createClient()
